import { useState } from "react";
import { Trash2, CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { prettyDate } from "@/lib/geo";

export const TripsDialog = ({ v, b, children, testId }) => {
  const [open, setOpen] = useState(false);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({ date: "", note: "", cities: [] });

  const onOpen = (o) => {
    if (o) { setTrips(v.trips || []); setForm({ date: "", note: "", cities: [] }); }
    setOpen(o);
  };
  const add = (e) => {
    e.preventDefault();
    if (!form.date) return;
    setTrips([...trips, form].sort((a, c) => a.date.localeCompare(c.date)));
    setForm({ date: "", note: "", cities: [] });
  };
  const toggleCity = (c) => setForm({ ...form, cities: form.cities.includes(c) ? form.cities.filter((x) => x !== c) : [...form.cities, c] });
  const save = async () => {
    const all = form.date ? [...trips, form] : trips;
    await b.patchVisited(v.id, { trips: all.sort((a, c) => a.date.localeCompare(c.date)) }, "Visit dates saved");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <p className="eyebrow">Visits</p>
          <DialogTitle className="font-display text-2xl">{v.country}</DialogTitle>
          <DialogDescription>When were you there? Every visit shows up on your timeline.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 max-h-56 overflow-y-auto">
          {trips.length === 0 && <li className="text-sm text-slate-500">No dates yet.</li>}
          {trips.map((t, i) => (
            <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2" data-testid={`trip-item-${i}`}>
              <div className="min-w-0">
                <p className="font-mono text-xs text-emerald-300">{prettyDate(t.date)}</p>
                {t.note && <p className="text-sm text-slate-300 truncate">{t.note}</p>}
                {t.cities?.length > 0 && <p className="text-[11px] text-emerald-200/70 truncate">{t.cities.join(" · ")}</p>}
              </div>
              <button onClick={() => setTrips(trips.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400" data-testid={`trip-delete-${i}`} aria-label="Remove visit"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="grid grid-cols-[auto_1fr] gap-2 rounded-xl bg-white/[0.03] p-3 border border-white/5">
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono text-xs [color-scheme:dark]" data-testid="trip-date-input" />
          <Input placeholder="Note (e.g. honeymoon)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="trip-note-input" />
          {v.cities.length > 0 && (
            <div className="col-span-2">
              <p className="text-[11px] text-slate-500 mb-1.5">Cities on this trip</p>
              <div className="flex flex-wrap gap-1.5">
                {v.cities.map((c) => (
                  <button type="button" key={c} onClick={() => toggleCity(c)} data-testid={`trip-city-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${form.cities.includes(c) ? "bg-emerald-400 text-[#04120c] border-emerald-300" : "border-white/10 text-slate-400 hover:text-white"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          <Button type="submit" variant="outline" size="sm" className="col-span-2 bg-transparent" data-testid="trip-add-button"><CalendarPlus size={14} /> Add visit</Button>
        </form>
        <DialogFooter>
          <Button onClick={save} className="bg-emerald-500 text-[#04120c] hover:bg-emerald-400" data-testid="trips-save-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
